import React, { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from '../ui/command';
import { cn } from '../../lib/utils';

// A searchable customer picker.
//
// This replaces a plain <Select>, which rendered all ~140 customers as one
// scrolling list with no way to search — you had to hunt for the name by eye.
// Searching covers name, account number, phone, email and address, because in
// the field a job is just as often identified by "the Vermont Ave apartments"
// or a phone number as by the account holder's name.
const haystack = (c) =>
  [c.name, c.company, c.accountNumber, c.phone, c.email, c.address, c.fullAddress, c.city, c.zip]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

// Digits only, so "(562) 215-8002", "562-215-8002" and "5622158002" all match.
const digits = (s) => String(s || '').replace(/\D/g, '');

// Exported so the matching rule can be exercised on its own, and so the
// customer list page can search by exactly the same rule as the job form —
// a customer you cannot find when booking is not findable anywhere else either.
export function matchesCustomer(customer, query) {
  const term = String(query || '').trim().toLowerCase();
  if (!term) return true;
  if (haystack(customer).includes(term)) return true;
  const termDigits = digits(term);
  return termDigits.length >= 3 && digits(customer.phone).includes(termDigits);
}

export default function CustomerCombobox({
  customers = [],
  value,                       // selected customer id
  onSelect,                    // (customer) => void
  placeholder = 'Search customers…',
  disabled = false,
  testId = 'customer-combobox',
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  const selected = useMemo(
    () => customers.find((c) => c.id === value) || null,
    [customers, value]
  );

  const results = useMemo(() => {
    if (!q.trim()) return customers.slice(0, 200);
    return customers.filter((c) => matchesCustomer(c, q)).slice(0, 200);
  }, [customers, q]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            'mt-1 h-10 w-full flex items-center justify-between rounded-md border border-slate-200',
            'bg-white px-3 text-sm text-left disabled:opacity-50'
          )}
        >
          <span className={cn('truncate', !selected && 'text-slate-400')}>
            {selected ? selected.name : placeholder}
          </span>
          <ChevronsUpDown className="w-4 h-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[260px]" align="start">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="w-4 h-4 shrink-0 opacity-50" />
            <CommandInput
              value={q}
              onValueChange={setQ}
              placeholder="Name, account #, phone, address…"
              className="h-10 border-0 focus:ring-0"
              data-testid={`${testId}-input`}
            />
          </div>
          <CommandList className="max-h-72">
            <CommandEmpty>No customer matches “{q}”.</CommandEmpty>
            {results.map((c) => (
              <CommandItem
                key={c.id}
                value={c.id}
                onSelect={() => {
                  onSelect?.(c);
                  setOpen(false);
                  setQ('');
                }}
                className="flex items-start gap-2"
              >
                <Check
                  className={cn('w-4 h-4 mt-0.5 shrink-0', value === c.id ? 'opacity-100' : 'opacity-0')}
                />
                <span className="min-w-0">
                  <span className="block font-medium truncate">{c.name}</span>
                  <span className="block text-[11px] text-slate-500 truncate">
                    {[c.accountNumber && `#${c.accountNumber}`, c.phone, c.address]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
